import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack } from "@mui/material";
import { Form, Formik } from "formik";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { useMutation, useQuery } from "react-query";
import { ApplicationsService } from "gen/clients";
import * as Yup from "yup";

interface Props {
  applicationId: string;
  selectedPresetId: string;
  currentPresetName: string;
  existingPresetNames: string[];
  onClose: () => void;
  onRefresh: () => void;
}

enum FieldName {
  presetName = "presetName"
}

interface FormValues {
  [FieldName.presetName]: string;
}

const RenamePresetDialog: React.FC<Props> = ({
  applicationId,
  selectedPresetId,
  currentPresetName,
  existingPresetNames,
  onClose,
  onRefresh
}) => {
  const {
    data: application,
    isLoading: isApplicationLoading,
    error: applicationError
  } = useQuery(["getApplication", applicationId], {
    queryFn: () => ApplicationsService.getApplication({ id: applicationId }),
    refetchOnMount: true
  });

  const {
    mutateAsync,
    isLoading: isMutating,
    error: mutationError
  } = useMutation(ApplicationsService.updateApplication, {
    onSuccess: () => {
      onRefresh();
      onClose();
    }
  });

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        [FieldName.presetName]: Yup.string()
          .required("Preset name is required")
          .test(
            "unique",
            "A preset with this name already exists",
            value => !value || value.trim() === currentPresetName || !existingPresetNames.includes(value.trim())
          )
      }),
    [existingPresetNames, currentPresetName]
  );

  const initialValues = React.useMemo<FormValues>(
    () => ({
      [FieldName.presetName]: currentPresetName
    }),
    [currentPresetName]
  );

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      if (!application || !selectedPresetId) {
        return;
      }

      const newPresetName = values[FieldName.presetName].trim();
      if (!newPresetName) {
        return;
      }

      // Update the preset with the new name
      const updatedPresets = (application.settingPresets || []).map(preset =>
        preset.id === selectedPresetId ? { ...preset, name: newPresetName } : preset
      );

      // Update application with renamed preset
      const updatedApplication = {
        ...application,
        settingPresets: updatedPresets
      };

      await mutateAsync({
        id: application.id,
        requestBody: updatedApplication
      });
    },
    [application, selectedPresetId, mutateAsync]
  );

  // Show loading state while fetching application data
  if (isApplicationLoading) {
    return (
      <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="xs">
        <DialogTitle>Rename Preset</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ minHeight: 100, justifyContent: "center", alignItems: "center" }}>
            Loading...
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  // Show error if application failed to load
  if (applicationError) {
    return (
      <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="xs">
        <DialogTitle>Rename Preset</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <ApiErrorMessage apiError={applicationError} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="xs">
      <DialogTitle>Rename Preset</DialogTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnMount={false}
        validateOnChange={true}
      >
        {() => (
          <Form noValidate={true} autoComplete="off">
            <DialogContent>
              <Stack spacing={3}>
                <TextInputField
                  name={FieldName.presetName}
                  label="Preset Name"
                  required={true}
                  helperText="Enter a new name for this preset"
                />
                {mutationError && <ApiErrorMessage apiError={mutationError} />}
              </Stack>
            </DialogContent>
            <DialogActions>
              <LoadingButton type="submit" color="primary" variant="contained" isLoading={isMutating}>
                Rename
              </LoadingButton>
              <Button color="secondary" onClick={onClose} disabled={isMutating}>
                Cancel
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { RenamePresetDialog };
